import React, { useState } from 'react';
import Layout from '../../components/admin/Layout';
import DataTable from '../../components/admin/DataTable';
import DetailDrawer from '../../components/admin/DetailDrawer';
import {
  Button, Field, StatusBadge, formatDate, relativeTime,
  GENDER_LABELS, DEGREE_LABELS
} from '../../components/admin/ui';
import useAdminResource from '../../hooks/useAdminResource';
import API_URL from '../../config/api';

const STATUSES = ['pending', 'reviewed', 'shortlisted', 'rejected'];

const CVs = () => {
  const controller = useAdminResource({ resource: 'cvs' });
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const openRow = (row) => {
    setActive(row);
    setNotes(row.notes || '');
  };

  const applyStatus = async (status) => {
    if (!active) return;
    setSaving(true);
    const ok = await controller.updateRow(active.id, { status, notes }, 'Candidate updated');
    setSaving(false);
    if (ok) setActive({ ...active, status });
  };

  const saveNotes = async () => {
    if (!active) return;
    setSaving(true);
    await controller.updateRow(active.id, { status: active.status, notes }, 'Notes saved');
    setSaving(false);
  };

  const resumeUrl = (row) => (row.resume?.path ? `${API_URL}/${row.resume.path}` : null);

  const columns = [
    {
      key: 'firstName',
      label: 'Candidate',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{row.firstName} {row.lastName}</p>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {[row.gender && GENDER_LABELS[row.gender], row.city].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Contact',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <a
            href={`mailto:${row.email}`}
            onClick={(e) => e.stopPropagation()}
            className="block truncate text-indigo-600 hover:underline"
          >
            {row.email}
          </a>
          {row.phone && (
            <a
              href={`tel:${row.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="mt-0.5 block text-xs text-slate-500 hover:text-slate-900"
            >
              {row.phone}
            </a>
          )}
        </div>
      )
    },
    {
      key: 'position',
      label: 'Profile',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-slate-900">{row.position || '—'}</p>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {[row.company, row.experience && `${row.experience} yrs`, row.currentCTC]
              .filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      key: 'createdAt',
      label: 'Received',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="text-slate-500" title={formatDate(row.createdAt, true)}>
          {relativeTime(row.createdAt)}
        </span>
      )
    },
    {
      key: 'resume',
      label: 'CV',
      render: (row) => (resumeUrl(row) ? (
        <a
          href={resumeUrl(row)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
        >
          📄 Open
        </a>
      ) : <span className="text-slate-300">—</span>)
    }
  ];

  return (
    <Layout>
      <DataTable
        title="Resumes"
        description="Candidate CVs submitted through the website"
        icon="📄"
        controller={controller}
        columns={columns}
        statuses={STATUSES}
        bulkStatuses={STATUSES}
        onRowClick={openRow}
        searchPlaceholder="Search name, email, city, company…"
        emptyTitle="No CVs yet"
        emptyDescription="Submissions from the Submit CV form will appear here."
      />

      <DetailDrawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active ? `${active.firstName} ${active.lastName}` : ''}
        subtitle={active?.position || 'Candidate'}
        status={active?.status}
        statuses={STATUSES}
        onStatusChange={applyStatus}
        footer={active && (
          <>
            {resumeUrl(active) && (
              <Button
                variant="secondary"
                onClick={() => window.open(resumeUrl(active), '_blank', 'noopener')}
              >
                📄 View resume
              </Button>
            )}
            <Button variant="primary" onClick={saveNotes} disabled={saving}>
              {saving ? 'Saving…' : 'Save notes'}
            </Button>
          </>
        )}
      >
        {active && (
          <div className="space-y-6">
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Personal
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <Field label="First name">{active.firstName}</Field>
                <Field label="Last name">{active.lastName}</Field>
                <Field label="Gender">{GENDER_LABELS[active.gender] || active.gender}</Field>
                <Field label="Date of birth">{formatDate(active.dob)}</Field>
                <Field label="City">{active.city}</Field>
                <Field label="Highest degree">{DEGREE_LABELS[active.degree] || active.degree}</Field>
              </dl>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Contact
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <Field label="Email">
                  <a href={`mailto:${active.email}`} className="text-indigo-600 hover:underline">
                    {active.email}
                  </a>
                </Field>
                <Field label="Phone">
                  {active.phone && (
                    <a href={`tel:${active.phone}`} className="text-indigo-600 hover:underline">
                      {active.phone}
                    </a>
                  )}
                </Field>
              </dl>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Professional
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <Field label="Current company">{active.company}</Field>
                <Field label="Designation">{active.position}</Field>
                <Field label="Experience">{active.experience && `${active.experience} years`}</Field>
                <Field label="Current CTC">{active.currentCTC}</Field>
              </dl>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Internal notes
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Add a note about this candidate…"
                className="w-full rounded-xl border-0 bg-slate-50 p-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500"
              />
              {active.reviewer && (
                <p className="mt-2 text-xs text-slate-400">
                  Last reviewed by {active.reviewer.name} · {formatDate(active.reviewedAt, true)}
                </p>
              )}
            </section>

            <section className="rounded-xl bg-slate-50 p-4">
              <dl className="grid grid-cols-2 gap-4">
                <Field label="Submitted">{formatDate(active.createdAt, true)}</Field>
                <Field label="Reference">#{active.id}</Field>
              </dl>
            </section>
          </div>
        )}
      </DetailDrawer>
    </Layout>
  );
};

export default CVs;
